import { useCallback, useState } from 'react';

import { forgetTokens, request } from '../../shared/api';
import { OFFER_URL, POLICY_URL, SUPPORT_URL } from '../../shared/links';
import {
  delay,
  expiry,
  fill,
  lockedFeatures,
  planName,
  quota,
  resets,
  type Limits,
  type Profile,
} from '../../shared/plan';
import { ListSkeleton, renderFailure } from '../../shared/states';
import { useLoad } from '../../shared/use-load';
import { useUpgrade } from '../../shared/upgrade';
import { openLink } from '../../telegram/webapp';
import { ReferralScreen } from '../referral/ReferralScreen';
import { WalletScreen } from '../wallet/WalletScreen';
import { CallsScreen } from '../calls/CallsScreen';
import { NotificationsScreen } from './NotificationsScreen';
import { StatesDemo } from './StatesDemo';

/**
 * Профиль (T11.10, `09_UI_UX.md` §4.5).
 *
 * Состав задан документом: тариф со сроком и потреблением лимитов,
 * избранное, история уведомлений, язык, поддержка, документы, удаление
 * учётной записи. Реферальная программа, кошелёк и AI-звонки — фаза 9,
 * и заглушек им здесь не поставлено: пустая строка меню обещает то,
 * чего нет.
 *
 * ## Тариф и лимиты — два запроса, а не один
 *
 * Так они разделены и на сервере (`modules/users/users.controller.ts`):
 * профиль меняется редко, потребление — с каждым уведомлением. Идут они
 * рядом и показываются вместе; отказ любого — отказ экрана, потому что
 * тариф без потребления и потребление без тарифа одинаково
 * бессмысленны.
 *
 * ## Удаление стирает и токены
 *
 * Иначе приложение осталось бы с пропуском от учётной записи, которой
 * нет, и следующий запрос получил бы 401 — то есть предложение войти
 * заново в только что удалённое.
 */

/**
 * Кошелёк (T16.5).
 *
 * Остаток и бесплатные звонки приходят одним снимком — так устроен
 * договор: два запроса дали бы два снимка разного момента, и решение
 * «звонить или пополнять» принималось бы по устаревшему остатку.
 */
interface WalletSnapshot {
  readonly balance_minor: string;
  readonly currency: string;
  readonly free_ai_calls: number;
  readonly free_ai_calls_left: number;
}

interface Everything {
  readonly profile: Profile;
  readonly limits: Limits;
  readonly wallet: WalletSnapshot;
}

function loadEverything(): Promise<Everything> {
  return Promise.all([
    request<Profile>('/v1/users/me'),
    request<Limits>('/v1/users/me/limits'),
    request<WalletSnapshot>('/v1/wallet'),
  ]).then(([profile, limits, wallet]) => ({ profile, limits, wallet }));
}

/**
 * Остаток в рублях.
 *
 * Копейки переводятся здесь, а не на сервере: деньги обязаны ходить
 * целым числом, иначе дробная арифметика однажды потеряет копейку, — а
 * показывать человеку всё равно надо рубли.
 */
function walletAmount(minor: string, currency: string): string {
  const major = Number(minor) / 100;
  const suffix = currency === 'RUB' ? ' ₽' : ` ${currency}`;

  return `${major.toLocaleString('ru-RU', { minimumFractionDigits: 2 })}${suffix}`;
}

/** Строка потребления: число, а не доля, и полоса рядом с ним. */
function Usage({
  title,
  value,
  part,
  note,
}: {
  title: string;
  value: string;
  part: number;
  note?: string;
}): React.JSX.Element {
  return (
    <div className="usage">
      <span className="usage__title">{title}</span>
      <span className="usage__value">{value}</span>
      <span className="usage__bar">
        <span className="usage__fill" style={{ width: `${String(Math.round(part * 100))}%` }} />
      </span>
      {note === undefined ? null : <span className="usage__note">{note}</span>}
    </div>
  );
}

/**
 * Тариф, срок и потребление.
 *
 * Ограничения названы до того, как человек в них упрётся
 * (`09_UI_UX.md` §8): счётчик подписок стоит рядом со своим пределом, а
 * задержка доставки — там же, где всё остальное, потому что она и есть
 * главное отличие тарифов.
 */
function Plan({ limits, onUpgrade }: { limits: Limits; onUpgrade: () => void }): React.JSX.Element {
  const now = new Date();
  const until = expiry(limits.plan_expires_at, now);
  const locked = lockedFeatures(limits.features);
  const reset = resets(limits.notifications.resets_at, now);

  return (
    <div className="card plan">
      <div className="plan__head">
        <span className="plan__name">{planName(limits.plan)}</span>
        {until === null ? null : <span className="plan__until">{until}</span>}
      </div>

      <Usage
        title="Подписки"
        value={quota(limits.subscriptions.used, limits.subscriptions.limit)}
        part={fill(limits.subscriptions.used, limits.subscriptions.limit)}
      />

      <Usage
        title="Уведомления за сутки"
        value={quota(limits.notifications.sent_in_period, limits.notifications.limit)}
        part={fill(limits.notifications.sent_in_period, limits.notifications.limit)}
        {...(reset === null ? {} : { note: reset })}
      />

      <p className="plan__delay">{delay(limits.notifications.delay_seconds)}</p>

      {locked.length === 0 ? null : (
        <>
          <p className="plan__locked">
            На Pro открывается: {locked.join(', ')}
            {limits.price_history_days === null
              ? ''
              : `; история цены сейчас видна за ${String(limits.price_history_days)} дней`}
            .
          </p>

          <button type="button" className="button button--accent" onClick={onUpgrade}>
            Подключить Pro
          </button>
        </>
      )}
    </div>
  );
}

/**
 * Строка меню.
 *
 * Без действия — не кнопка, а карточка: кнопка, которая ничего не
 * делает, выглядит сломанной, и человек жмёт её несколько раз, прежде
 * чем поверит.
 */
function Row({
  title,
  note,
  onClick,
}: {
  title: string;
  note?: string;
  onClick?: () => void;
}): React.JSX.Element {
  if (onClick === undefined) {
    return (
      <div className="card menu menu--idle">
        <span className="menu__title">{title}</span>
        {note === undefined ? null : <span className="menu__note">{note}</span>}
      </div>
    );
  }

  return (
    <button type="button" className="card menu" onClick={onClick}>
      <span className="menu__title">{title}</span>
      {note === undefined ? null : <span className="menu__note">{note}</span>}
      <span className="menu__arrow" aria-hidden="true">
        ›
      </span>
    </button>
  );
}

/**
 * Строка со ссылкой наружу.
 *
 * Отдельно от `Row` не ради удобства: адрес приходит из переменной
 * сборки и бывает пустым, а обработчик, замкнувший `string | null`,
 * пришлось бы проверять внутри себя — то есть уже после нажатия.
 * Здесь пусто и непусто различаются до отрисовки, и строка без адреса
 * не притворяется кнопкой.
 */
function LinkRow({
  title,
  url,
  missing,
}: {
  title: string;
  url: string | null;
  /** Чем объяснить отсутствие адреса: «готовится», «не задан». */
  missing: string;
}): React.JSX.Element {
  if (url === null) return <Row title={title} note={missing} />;

  return (
    <Row
      title={title}
      onClick={() => {
        openLink(url);
      }}
    />
  );
}

/**
 * Часовой пояс.
 *
 * Не украшение профиля: по нему считаются сутки квоты и обнуление
 * счётчика уведомлений (`modules/users/quotas.ts`). Пояс, оставшийся от
 * прежнего места жизни, сдвигает человеку полночь на несколько часов —
 * и он видит «обнулится через 9 ч» там, где ждал утра.
 *
 * Приложение не меняет пояс само: человек может открыть его из отпуска,
 * и тихая подмена сдвинула бы ему квоту на неделю. Показывается
 * расхождение и предлагается действие.
 */
function Timezone({ current }: { current: string }): React.JSX.Element {
  const [zone, setZone] = useState(current);
  const [saving, setSaving] = useState(false);
  const device = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const take = useCallback(() => {
    setSaving(true);
    request<Profile>('/v1/users/me', { method: 'PATCH', body: { timezone: device } })
      .then((updated) => {
        setZone(updated.timezone);
      })
      .catch(() => {
        // Пояс остался прежним, и он же показан: сделать вид, что смена
        // прошла, значит объяснять человеку неверную полночь
      })
      .finally(() => {
        setSaving(false);
      });
  }, [device]);

  return (
    <div className="card menu menu--idle">
      <span className="menu__title">Часовой пояс</span>
      <span className="menu__note">{zone}</span>
      {device === zone || device === '' ? null : (
        <button type="button" className="menu__action" disabled={saving} onClick={take}>
          {saving ? 'Меняю…' : `Взять с телефона (${device})`}
        </button>
      )}
    </div>
  );
}

/**
 * Удаление учётной записи (T6.4, L-6).
 *
 * Два шага, а не один: удаление необратимо, а кнопка стоит на экране,
 * который открывают ради тарифа и избранного. Второй шаг называет
 * последствия — иначе подтверждение подтверждает неизвестно что.
 *
 * Подтверждение своё, а не `confirm()` браузера: в Mini App системное
 * окно перехватывает Telegram, и ведут себя его клиенты по-разному —
 * вплоть до того, что окна не видно вовсе.
 */
function DeleteAccount({ onDone }: { onDone: (payments: number) => void }): React.JSX.Element {
  const [asked, setAsked] = useState(false);
  const [going, setGoing] = useState(false);
  const [failed, setFailed] = useState(false);

  const remove = useCallback(() => {
    setGoing(true);
    setFailed(false);
    request<{ anonymised_payments: number }>('/v1/users/me', { method: 'DELETE' })
      .then((report) => {
        // Пропуск от удалённой учётной записи живёт ещё четверть часа;
        // оставить его значит показать человеку вход вместо прощания
        forgetTokens();
        onDone(report.anonymised_payments);
      })
      .catch(() => {
        setFailed(true);
      })
      .finally(() => {
        setGoing(false);
      });
  }, [onDone]);

  if (!asked) {
    return (
      <button
        type="button"
        className="card menu menu--danger"
        onClick={() => {
          setAsked(true);
        }}
      >
        <span className="menu__title">Удалить аккаунт</span>
      </button>
    );
  }

  return (
    <div className="card menu--danger">
      <p className="menu__title">Удалить аккаунт?</p>
      <p className="menu__note">
        Подписки, избранное и история уведомлений будут стёрты, уведомления перестанут приходить.
        Отменить это нельзя. Записи о платежах закон велит сохранить — они останутся без связи с
        вами.
      </p>

      {failed ? <p className="menu__note">Не получилось. Попробуйте ещё раз.</p> : null}

      <div className="menu__buttons">
        <button type="button" className="button" disabled={going} onClick={remove}>
          {going ? 'Удаляю…' : 'Да, удалить'}
        </button>
        {/*
          «Оставить» выделено, а «удалить» — нет. Заметная кнопка
          выбирается быстрее, и быстрым здесь должен быть отказ от
          необратимого
        */}
        <button
          type="button"
          className="button button--accent"
          disabled={going}
          onClick={() => {
            setAsked(false);
          }}
        >
          Оставить
        </button>
      </div>
    </div>
  );
}

/** Что видно после удаления: приложению больше нечего показывать. */
function Farewell({ payments }: { payments: number }): React.JSX.Element {
  return (
    <div className="state">
      <p className="state__title">Аккаунт удалён</p>
      <p className="state__hint">
        Данные стёрты
        {payments === 0 ? '' : `, записи о платежах (${String(payments)}) обезличены`}. Закройте
        приложение — уведомления больше не придут. Захотите вернуться — откройте бота заново.
      </p>
    </div>
  );
}

export function ProfileScreen({
  onFavorites,
}: {
  /** Переход на вкладку избранного: список живёт там. */
  onFavorites: () => void;
}): React.JSX.Element {
  const [state, retry] = useLoad<Everything>(useCallback(loadEverything, []));
  const [inside, setInside] = useState<
    'profile' | 'notifications' | 'states' | 'wallet' | 'referral' | 'calls'
  >('profile');
  const [removed, setRemoved] = useState<number | null>(null);
  const onUpgrade = useUpgrade();

  const toProfile = useCallback(() => {
    setInside('profile');
  }, []);

  const fromWallet = useCallback(() => {
    setInside('profile');
    retry();
  }, [retry]);

  if (removed !== null) return <Farewell payments={removed} />;

  // Возврат из кошелька перечитывает профиль: человек мог там
  // пополниться, и строка «Кошелёк» иначе показала бы остаток до
  // пополнения — то есть деньги, которых уже не столько
  if (inside === 'referral') return <ReferralScreen onBack={toProfile} />;

  if (inside === 'calls') return <CallsScreen onBack={toProfile} />;
  if (inside === 'wallet') return <WalletScreen onBack={fromWallet} />;

  if (inside === 'notifications') return <NotificationsScreen onBack={toProfile} />;

  if (inside === 'states') return <StatesDemo onBack={toProfile} />;

  if (state.kind === 'loading') return <ListSkeleton rows={3} />;

  if (state.kind === 'failed') {
    return <>{renderFailure(state.error, { onRetry: retry, onUpgrade })}</>;
  }

  const { profile, limits, wallet } = state.value;

  return (
    <>
      <Plan limits={limits} onUpgrade={onUpgrade} />

      {/*
        Кошелёк показывается всегда, даже пустой. Скрывать нулевой
        остаток значит прятать от человека само существование кошелька —
        и он не узнает, что пополнение возможно, ровно до того дня,
        когда деньги понадобятся немедленно.

        Бесплатные звонки стоят рядом не для красоты: перед звонком
        важно не «сколько денег», а «спишут ли вообще».
      */}
      <Row
        title="Кошелёк"
        note={
          wallet.free_ai_calls_left > 0
            ? `${walletAmount(wallet.balance_minor, wallet.currency)} · бесплатных звонков ${String(wallet.free_ai_calls_left)}`
            : walletAmount(wallet.balance_minor, wallet.currency)
        }
        onClick={() => {
          setInside('wallet');
        }}
      />

      {/*
        Приглашения стоят сразу за кошельком: это второй способ
        получить деньги на счёт, и разносить их по разным углам экрана
        значит прятать половину от того, кто нашёл первую
      */}
      <Row
        title="Приглашайте друзей"
        note="10 % с платежей приглашённых"
        onClick={() => {
          setInside('referral');
        }}
      />

      <Row
        title="Звонки"
        note="Отчёты AI-звонков продавцам"
        onClick={() => {
          setInside('calls');
        }}
      />

      <Row title="Избранное" onClick={onFavorites} />
      <Row
        title="История уведомлений"
        onClick={() => {
          setInside('notifications');
        }}
      />

      {/*
        Язык показан, но не переключается. Перевода нет ни в приложении,
        ни в боте, и переключатель, меняющий поле в базе и ничего на
        экране, — обещание, которого мы не выполняем. Строка остаётся,
        чтобы человек видел, на каком языке с ним говорят
      */}
      <Row title="Язык" note={profile.language === 'ru' ? 'Русский' : profile.language} />

      <Timezone current={profile.timezone} />

      <LinkRow title="Поддержка" url={SUPPORT_URL} missing="адрес не задан в сборке" />
      <LinkRow title="Политика обработки данных" url={POLICY_URL} missing="готовится" />
      <LinkRow title="Оферта" url={OFFER_URL} missing="готовится" />

      <DeleteAccount onDone={setRemoved} />

      {/*
        Показ состояний экранов (T11.3). Требование задачи — состояния не
        только реализованы, но и продемонстрированы; держать показ в
        профиле честнее, чем в спрятанном служебном экране: спрятанное
        перестают открывать, и оно тихо расходится с настоящими экранами
      */}
      <Row
        title="Состояния экранов"
        note="загрузка, пустота, отказы"
        onClick={() => {
          setInside('states');
        }}
      />
    </>
  );
}
